"use client";

import {
  IconArrowLeft,
  IconChevronDown,
  IconCircle,
} from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import { type ReactNode, useState } from "react";
import { BottomSheetDrawer } from "@/components/bottom-sheet-drawer";
import { RadioSelect } from "@/components/radio-select";
import { Button } from "@/components/ui/button";
import { useMobile } from "@/lib/hooks/use-mobile";
import { cn } from "@/lib/utils";

type CalorificOption = {
  id: string;
  calories: number;
};

type DrawerItemProps = {
  label: string | ReactNode;
  value: string;
  selectedValue: string;
  onSelect: (value: string) => void;
};

const DrawerItem = ({
  label,
  value,
  selectedValue,
  onSelect,
}: DrawerItemProps) => {
  const isSelected = value === selectedValue;

  return (
    <button
      aria-pressed={isSelected}
      className={cn(
        "flex w-full cursor-pointer items-center gap-3 p-4 text-left transition-colors",
        "hover:bg-brand-green/10",
        isSelected && "font-medium",
      )}
      onClick={() => onSelect(value)}
      tabIndex={0}
      type="button"
    >
      <span
        className={cn(
          "flex size-5 shrink-0 items-center justify-center rounded-full border",
          "bg-brand-white",
          isSelected && "border-brand-green bg-brand-green",
        )}
      >
        {isSelected && (
          <IconCircle
            className="h-3 w-3 fill-current text-brand-white"
            stroke={1.25}
          />
        )}
      </span>
      <span className="flex-1">{label}</span>
    </button>
  );
};

type DietMenuCalorificSelectProps = {
  calorificOptions: CalorificOption[];
  calories: CalorificOption;
  onCalorificChange: (id: string) => void;
};

export const DietMenuCalorificSelect = ({
  calorificOptions,
  calories,
  onCalorificChange,
}: DietMenuCalorificSelectProps) => {
  const t = useTranslations();
  const { isMobile } = useMobile();
  const [selectedCalorificId, setSelectedCalorificId] = useState<string>(
    calories.id,
  );
  const [drawerOpen, setDrawerOpen] = useState(false);

  const sortedOptions = [...calorificOptions].sort(
    (a, b) => a.calories - b.calories,
  );

  if (isMobile) {
    return (
      <BottomSheetDrawer
        closeClassName="left-4"
        closeIcon={<IconArrowLeft className="h-6 w-6" stroke={1.25} />}
        content={
          <div>
            {sortedOptions.map((option) => (
              <DrawerItem
                key={option.id}
                label={`${option.calories} kcal`}
                onSelect={() => {
                  setSelectedCalorificId(option.id);
                }}
                selectedValue={selectedCalorificId}
                value={option.id}
              />
            ))}
          </div>
        }
        footer={
          <Button
            className="mt-auto h-14 w-full"
            data-pw="apply-filters"
            onClick={() => {
              onCalorificChange(selectedCalorificId);
              setDrawerOpen(false);
            }}
            type="button"
            variant="secondary"
          >
            {t("filters.apply")}
          </Button>
        }
        fullHeight
        onOpenChange={setDrawerOpen}
        open={drawerOpen}
        title={t("diets.filters.labels.calories")}
        trigger={
          <button
            className="flex h-14 w-full max-w-[150px] xs:max-w-[179px] items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            tabIndex={0}
            type="button"
          >
            <p className="flex flex-col items-start">
              <span className="text-gray-500 text-sm">
                {t("diets.filters.labels.calories")}
              </span>
              <span className="font-medium">{calories.calories} kcal</span>
            </p>

            <IconChevronDown className="h-6 w-6" stroke={1.25} />
          </button>
        }
      />
    );
  }

  return (
    <RadioSelect
      className="h-14 max-w-[150px] xs:max-w-[179px] md:max-w-[360px]"
      label={t("diets.filters.labels.calories")}
      onValueChange={onCalorificChange}
      options={sortedOptions.map((option) => ({
        value: option.id,
        label: `${option.calories} kcal`,
      }))}
      value={calories.id}
    />
  );
};
